import type { Cue } from '@core/model/cue.ts';
import type { Settings } from '@core/settings/schema.ts';

export type FuriganaSettings = Pick<
  Settings,
  'furiganaEnable' | 'furiganaConvertTo' | 'furiganaMode' | 'furiganaRomaji'
>;

/** Same remote dict as extension `kuroshiro.js`. */
export const KUROMOJI_DICT_PATH =
  'https://netflixsubs.app/scache/netflixsubs-app-downloads/kuromoji-dict';

const FURIGANA_MODE = 'furigana';

type KuroshiroInstance = {
  init: (analyzer: unknown) => Promise<void> | void;
  convert: (
    text: string,
    opts: { to: string; mode: string; romajiSystem?: string },
  ) => Promise<string>;
};

type KuroshiroCtor = new () => KuroshiroInstance;
type AnalyzerCtor = new (opts: { dictPath: string }) => unknown;

type WindowWithKuroshiro = Window & {
  Kuroshiro?: KuroshiroCtor | { default: KuroshiroCtor };
  KuromojiAnalyzer?: AnalyzerCtor | { default: AnalyzerCtor };
};

let initPromise: Promise<KuroshiroInstance | null> | null = null;
let converter: KuroshiroInstance | null = null;

/** True when a track language code looks Japanese. */
export function isJapaneseLanguage(code: string | undefined): boolean {
  return (code ?? '').toLowerCase().includes('ja');
}

function resolveCtor<T>(value: T | { default: T } | undefined): T | undefined {
  if (!value) return undefined;
  if (typeof value === 'function') return value as T;
  if (typeof value === 'object' && value && 'default' in value) {
    return value.default;
  }
  return undefined;
}

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (typeof document === 'undefined') {
      reject(new Error('No document for Kuroshiro scripts'));
      return;
    }
    const existing = document.querySelector<HTMLScriptElement>(`script[data-kuroshiro="${src}"]`);
    if (existing) {
      if (existing.dataset.loaded === '1') {
        resolve();
        return;
      }
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error(`Failed to load ${src}`)), {
        once: true,
      });
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.dataset.kuroshiro = src;
    script.onload = () => {
      script.dataset.loaded = '1';
      resolve();
    };
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(script);
  });
}

/**
 * Lazily load public/kuroshiro scripts + init with remote kuromoji dict.
 * Returns null if scripts/dict unavailable (caller falls back to plain text).
 * Failed inits clear the promise so a later enable can retry (extension polls).
 */
export async function ensureKuroshiro(
  dictPath = KUROMOJI_DICT_PATH,
  baseUrl = '/kuroshiro',
): Promise<KuroshiroInstance | null> {
  if (converter) return converter;
  if (initPromise) return initPromise;

  initPromise = (async () => {
    try {
      await loadScript(`${baseUrl}/kuroshiro.min.js`);
      await loadScript(`${baseUrl}/kuroshiro-analyzer-kuromoji.min.js`);
      const w = window as WindowWithKuroshiro;
      const Kuroshiro = resolveCtor(w.Kuroshiro);
      const KuromojiAnalyzer = resolveCtor(w.KuromojiAnalyzer);
      if (!Kuroshiro || !KuromojiAnalyzer) {
        initPromise = null;
        return null;
      }
      const instance = new Kuroshiro();
      const analyzer = new KuromojiAnalyzer({ dictPath });
      await instance.init(analyzer);
      converter = instance;
      return instance;
    } catch {
      initPromise = null;
      return null;
    }
  })();

  return initPromise;
}

/** Reset for tests. */
export function resetKuroshiroForTests(): void {
  initPromise = null;
  converter = null;
}

/** Convert one cue text; falls back to plain text on failure. */
export async function generateFurigana(
  text: string,
  settings: FuriganaSettings,
  opts?: { ensure?: typeof ensureKuroshiro },
): Promise<string> {
  const plain = text.replace(/<br[^>]*>/gi, '\n');
  const ensure = opts?.ensure ?? ensureKuroshiro;
  const kuro = await ensure();
  if (!kuro) return plain;
  try {
    return await kuro.convert(plain, {
      to: settings.furiganaConvertTo || 'hiragana',
      mode: FURIGANA_MODE,
      romajiSystem:
        settings.furiganaConvertTo === 'romaji' ? settings.furiganaRomaji : undefined,
    });
  } catch {
    return plain;
  }
}

/** Apply furigana HTML to cues when enabled (Japanese tracks only). */
export async function applyFuriganaToCues(
  cues: Cue[],
  settings: FuriganaSettings,
  languageCode?: string,
  opts?: { ensure?: typeof ensureKuroshiro },
): Promise<Cue[]> {
  if (!settings.furiganaEnable) {
    return cues.map(({ kuroshiro: _k, ...rest }) => rest as Cue);
  }
  if (languageCode !== undefined && !isJapaneseLanguage(languageCode)) {
    return cues.map(({ kuroshiro: _k, ...rest }) => rest as Cue);
  }

  const out: Cue[] = [];
  for (const cue of cues) {
    const kuroshiro = await generateFurigana(cue.text, settings, opts);
    out.push({ ...cue, kuroshiro });
  }
  return out;
}

/** Snapshot of settings that require cue reprocess (furigana + preprocess). */
export function cueProcessSettingsKey(settings: Settings): string {
  return [
    settings.furiganaEnable,
    settings.furiganaConvertTo,
    settings.furiganaRomaji,
    settings.preProcessing1,
    settings.preProcessing2,
    settings.preProcessing3,
    settings.practiceMode,
    settings.shadowingAutoRemoveLineBreaks !== false,
  ].join('|');
}
